import axios, { AxiosError } from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import router from '@/router'

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: true
})

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem('auth_token')
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status

      switch (status) {
        case 401: {
          // Skip for logout request itself to avoid infinite loop
          const reqUrl = error.config?.url || ''
          if (!reqUrl.includes('/logout')) {
            localStorage.removeItem('auth_token')
            delete api.defaults.headers.common['Authorization']
            window.dispatchEvent(new CustomEvent('auth:unauthenticated', {
              detail: { redirect: router.currentRoute.value.fullPath }
            }))
          }
          break
        }
          
        case 403:
          // Forbidden
          console.error('Access forbidden')
          break
          
        case 404:
          // Not found
          console.error('Resource not found')
          break
          
        case 422:
          // Validation error - let the component handle it
          break
          
        case 500:
          // Server error
          console.error('Server error occurred')
          break
      }
    } else if (error.request) {
      // Network error
      console.error('Network error - no response received')
    }
    
    return Promise.reject(error)
  }
)

export default api

// Helper functions for common HTTP methods
export const apiGet = <T>(url: string, params?: object) => 
  api.get<T>(url, { params })

export const apiPost = <T>(url: string, data?: object) => 
  api.post<T>(url, data)

export const apiPut = <T>(url: string, data?: object) => 
  api.put<T>(url, data)

export const apiDelete = <T>(url: string) => 
  api.delete<T>(url)

export const apiPatch = <T>(url: string, data?: object) => 
  api.patch<T>(url, data)
